import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { UserProfile, UserRole } from '@/hooks/useUserRole';

interface UserRoleContextType {
  profile: UserProfile | null;
  userRole: UserRole | null;
  loading: boolean;
  error: string | null;
}

const UserRoleContext = createContext<UserRoleContextType | null>(null);

export const useUserRoleContext = () => useContext(UserRoleContext);

export const UserRoleProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const fetchProfileAndRole = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session?.user) {
          if (mounted) {
            setProfile(null);
            setUserRole(null);
            setLoading(false);
          }
          return;
        }

        const user = session.user;

        // Fetch profile and role in parallel
        const [profileResult, roleResult] = await Promise.all([
          supabase.from('profiles').select('*').eq('user_id', user.id).single(),
          supabase.from('user_roles').select('role').eq('user_id', user.id).order('created_at', { ascending: true }).limit(1)
        ]);

        if (profileResult.error) throw profileResult.error;

        let resolvedRole: UserRole = 'farmer';
        if (roleResult.data && roleResult.data.length > 0) {
          resolvedRole = roleResult.data[0].role as UserRole;
        } else if ((profileResult.data as any)?.role) {
          resolvedRole = (profileResult.data as any).role as UserRole;
        }

        if (mounted) {
          setUserRole(resolvedRole);
          setProfile(profileResult.data);
          setLoading(false);
        }
      } catch (err) {
        console.error('UserRoleProvider: Error fetching user data:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch profile');
          setLoading(false);
        }
      }
    };

    fetchProfileAndRole();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      if (mounted) {
        setLoading(true);
        fetchProfileAndRole();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <UserRoleContext.Provider value={{ profile, userRole, loading, error }}>
      {children}
    </UserRoleContext.Provider>
  );
};
